import { Icon } from '@iconify/react';
import { UploadFileInfo } from '@progress/kendo-react-upload';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NotAvailableVirusScan from 'src/assets/img/not-available-virus-scan.png';
import NotAvailable from 'src/assets/img/not-available.png';
import DocumentCountTracker from 'src/components/DocumentCountTracker';
import Image from 'src/components/Image';
import PDFViewer from 'src/components/PDFViewer';
import { uint8ArrayToBase64 } from 'src/utils/fileTransformation';
import {
  inspectPdf,
  isImageUploadFile,
  isPdfFile,
  isPreviewableUploadFile,
  PdfInspectError,
} from 'src/utils/fileValidation';

type PreviewFileKind = 'pdf' | 'image' | null;

type PreviewStatus = 'idle' | 'loading' | 'ready' | 'error';

type PreviewErrorType = 'unable-to-open' | 'corrupt' | 'password-protected' | 'not-supported';

export interface UploadFilePreviewPanelProps {
  selectedFile: UploadFileInfo | null;
  previewableFiles: UploadFileInfo[];
  selectedPreviewIndex: number;
  onSelectPreviewIndex: (index: number) => void;
  previewHeight: string;
  isOcrCompany?: boolean;
  pdfInspectError?: PdfInspectError | null;
  useExternalScroll?: boolean;
}

const PreviewPlaceholder = ({
  message,
  variant = 'empty',
}: {
  message: string;
  variant?: 'empty' | 'invalid' | 'error-ocr' | 'error';
}) => (
  <div
    className={`flex h-full flex-col items-center justify-center gap-3 p-4 text-center ${
      variant === 'invalid' ? 'text-red-600' : 'text-gray-600'
    }`}
  >
    {variant === 'error-ocr' ? (
      <img src={NotAvailableVirusScan} alt="" className="max-w-[200px] px-12" />
    ) : variant === 'error' ? (
      <img src={NotAvailable} alt="" className="max-w-[200px]" />
    ) : variant === 'empty' || variant === 'invalid' ? (
      <div data-testid="preview-placeholder-icon">
        <Icon
          icon="lucide:file"
          width={48}
          height={48}
          className={variant === 'invalid' ? 'text-red-400' : 'text-gray-400'}
        />
      </div>
    ) : null}
    <p className={`text-sm font-medium ${variant === 'invalid' ? 'text-red-600' : ''}`}>
      {message}
    </p>
  </div>
);

const UploadFilePreviewPanel = ({
  selectedFile,
  previewableFiles,
  selectedPreviewIndex,
  onSelectPreviewIndex,
  previewHeight,
  isOcrCompany = false,
  pdfInspectError = null,
  useExternalScroll = false,
}: UploadFilePreviewPanelProps) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<PreviewStatus>('idle');
  const [errorType, setErrorType] = useState<PreviewErrorType | null>(null);
  const [previewKind, setPreviewKind] = useState<PreviewFileKind>(null);
  const [pdfBase64, setPdfBase64] = useState('');
  const [imageObjectUrl, setImageObjectUrl] = useState<string | null>(null);

  const total = previewableFiles.length;
  const currentSelected = selectedPreviewIndex >= 0 ? selectedPreviewIndex + 1 : 0;
  const disableDecrement = selectedPreviewIndex <= 0;
  const disableIncrement = selectedPreviewIndex < 0 || selectedPreviewIndex >= total - 1;

  const decrementFn = useCallback(() => {
    if (!disableDecrement) {
      onSelectPreviewIndex(selectedPreviewIndex - 1);
    }
  }, [disableDecrement, onSelectPreviewIndex, selectedPreviewIndex]);

  const incrementFn = useCallback(() => {
    if (!disableIncrement) {
      onSelectPreviewIndex(selectedPreviewIndex + 1);
    }
  }, [disableIncrement, onSelectPreviewIndex, selectedPreviewIndex]);

  const errorMessage = useMemo(() => {
    if (!errorType) {
      return '';
    }
    if (
      errorType === 'corrupt' ||
      errorType === 'unable-to-open' ||
      errorType === 'not-supported'
    ) {
      return t('uploadFiles.invalidFilePreview');
    }
    if (errorType === 'password-protected') {
      return t('scanByPage.passwordProtected');
    }
    return t('uploadFiles.invalidFilePreview');
  }, [errorType, t]);

  const errorVariant = useMemo((): 'invalid' | 'error-ocr' | 'error' | 'empty' => {
    if (!errorType) {
      return 'empty';
    }
    if (
      errorType === 'corrupt' ||
      errorType === 'unable-to-open' ||
      errorType === 'not-supported'
    ) {
      return 'invalid';
    }
    if (errorType === 'password-protected') {
      return isOcrCompany ? 'error-ocr' : 'error';
    }
    return 'invalid';
  }, [errorType, isOcrCompany]);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    const loadPreview = async () => {
      setPdfBase64('');
      setImageObjectUrl(null);
      setErrorType(null);
      setPreviewKind(null);

      if (!selectedFile) {
        setStatus('idle');
        return;
      }

      if (pdfInspectError) {
        setStatus('error');
        setErrorType(pdfInspectError === 'password-protected' ? 'password-protected' : 'corrupt');
        return;
      }

      if (!(await isPreviewableUploadFile(selectedFile))) {
        setStatus('error');
        setErrorType('not-supported');
        return;
      }

      const rawFile = selectedFile.getRawFile?.() as File | undefined;
      if (!rawFile) {
        setStatus('error');
        setErrorType('unable-to-open');
        return;
      }

      setStatus('loading');

      try {
        if (await isPdfFile(selectedFile)) {
          await inspectPdf(rawFile);
          const buffer = await rawFile.arrayBuffer();
          if (cancelled) {
            return;
          }
          setPdfBase64(uint8ArrayToBase64(new Uint8Array(buffer)));
          setPreviewKind('pdf');
          setStatus('ready');
          return;
        }

        if (await isImageUploadFile(selectedFile)) {
          objectUrl = URL.createObjectURL(rawFile);
          if (cancelled) {
            URL.revokeObjectURL(objectUrl);
            return;
          }
          setImageObjectUrl(objectUrl);
          setPreviewKind('image');
          setStatus('ready');
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
        const message = error instanceof Error ? error.message.toLowerCase() : '';
        setStatus('error');
        if (message.includes('password') || message.includes('encrypt')) {
          setErrorType('password-protected');
        } else {
          setErrorType('corrupt');
        }
      }
    };

    loadPreview();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [pdfInspectError, selectedFile]);

  useEffect(
    () => () => {
      if (imageObjectUrl) {
        URL.revokeObjectURL(imageObjectUrl);
      }
    },
    [imageObjectUrl]
  );

  const showNavigation = previewableFiles.length > 0 && selectedPreviewIndex >= 0;
  const viewerHeight = useExternalScroll ? '400px' : '100%';

  return (
    <div
      className={`flex flex-col bg-gray-100 ${
        useExternalScroll ? 'min-h-[450px] h-auto' : 'h-full min-h-0'
      }`}
      style={useExternalScroll ? undefined : { height: previewHeight }}
    >
      {showNavigation && (
        <DocumentCountTracker
          disableDecrement={disableDecrement}
          disableIncrement={disableIncrement}
          total={total}
          currentSelected={currentSelected}
          incrementFn={incrementFn}
          decrementFn={decrementFn}
          isOcr
        />
      )}
      <div
        className={`p-2 ${useExternalScroll ? 'min-h-[400px]' : 'min-h-0 flex-1 overflow-auto'}`}
      >
        {!selectedFile && <PreviewPlaceholder message={t('uploadFiles.selectFileToPreview')} />}
        {selectedFile && status === 'loading' && (
          <div className="flex h-full items-center justify-center">
            <span className="k-icon k-i-loading text-lg" />
          </div>
        )}
        {selectedFile && status === 'error' && errorType && (
          <PreviewPlaceholder message={errorMessage} variant={errorVariant} />
        )}
        {selectedFile && status === 'ready' && previewKind === 'pdf' && pdfBase64 && (
          <PDFViewer data={pdfBase64} fileName={selectedFile.name} height={viewerHeight} />
        )}
        {selectedFile && status === 'ready' && previewKind === 'image' && imageObjectUrl && (
          <Image
            imageUrl={imageObjectUrl}
            controls
            filename={selectedFile.name}
            isOcr={isOcrCompany}
            styles={{ maxHeight: previewHeight }}
          />
        )}
      </div>
    </div>
  );
};

export default UploadFilePreviewPanel;
